'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'
import { IntlProvider } from 'next-intl'
import pt from '../../messages/pt-br.json'
import en from '../../messages/en.json'

type SupportedLocale = 'pt' | 'en'
type Messages = typeof en

const messagesMap: Record<SupportedLocale, Messages> = { pt, en }

interface LocaleContextType {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<SupportedLocale>('en')
  const [messages, setMessages] = useState<Messages>(en)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const preferred = navigator.languages?.[0]?.split('-')[0] ?? 'en'
    const chosen: SupportedLocale = preferred === 'pt' ? 'pt' : 'en'
    setLocale(chosen)
    setMessages(messagesMap[chosen])
    setHydrated(true)
  }, [])

  const changeLocale = (newLocale: SupportedLocale) => {
    setLocale(newLocale)
    setMessages(messagesMap[newLocale])
  }

  if (!hydrated) return null

  return (
    <LocaleContext.Provider value={{ locale, setLocale: changeLocale }}>
      <IntlProvider locale={locale} messages={messages}>
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  )
}
